import { useTranslation } from 'react-i18next'
import type { OrgSwitcherLabels } from '@agion/ui'
import type { OrganizationErrorCode } from '@agion/auth'

/** Translated labels for every organization switcher in control-ui. */
export function useOrgSwitcherLabels(): OrgSwitcherLabels {
  const { t } = useTranslation('pages')
  return {
    selectOrg: t('orgSwitcher.selectOrg', { defaultValue: 'Select Organization' }),
    title: t('orgSwitcher.title', { defaultValue: 'Organizations' }),
    description: t('orgSwitcher.description', { defaultValue: 'Switch between your organizations' }),
    tooltip: t('orgSwitcher.tooltip', { defaultValue: 'Switch organization' }),
    retry: t('orgSwitcher.retry', { defaultValue: 'Retry' }),
  }
}

// agion/Agion#2321: the single control-ui mapping from @agion/auth's
// classified OrganizationErrorCode to one localized, actionable sentence
// (what happened + what to do next). @agion/auth owns no translated text —
// every control-ui surface that shows a failed organization fetch/switch
// (InfraOrgSwitcher, the access-denied page) reads it through
// useOrganizationErrorMessage() below. There must be no second mapping.
const ERROR_CODE_KEYS: Record<OrganizationErrorCode, string> = {
  forbidden: 'orgSwitcher.error.forbidden',
  not_found: 'orgSwitcher.error.notFound',
  network: 'orgSwitcher.error.network',
  server: 'orgSwitcher.error.server',
  unknown: 'orgSwitcher.error.unknown',
}

const ERROR_CODE_DEFAULTS: Record<OrganizationErrorCode, string> = {
  forbidden: "You don't have access to this organization. Ask its administrator to add you.",
  not_found: 'This organization could not be found. It may have been removed.',
  network: 'Could not reach the server. Check your connection and try again.',
  server: 'The server ran into a problem. Try again in a moment.',
  unknown: 'Something went wrong. Try again, and contact an administrator if it keeps happening.',
}

/** Maps a classified organization error code to one localized, actionable
 * sentence, or undefined when there is no error. */
export function useOrganizationErrorMessage(errorCode: OrganizationErrorCode | null): string | undefined {
  const { t } = useTranslation('pages')
  if (!errorCode) return undefined
  return t(ERROR_CODE_KEYS[errorCode], { defaultValue: ERROR_CODE_DEFAULTS[errorCode] })
}
